import {simpleTreatmentLabel,simpleReasonLabel,type SimpleProfitResolution} from './simple-profit-loss.ts';
import { accountName, financialSourceLabel, type ResolvedFinancialAccount } from './profit-loss.ts';
import type { Language, Transaction } from "./finance.ts";
import { recipientAccountLabel } from "./finance.ts";
import { transactionBrandName, type BusinessUnit } from "./business-units.ts";
import { categoryName, type FinanceCategory } from "./categories.ts";

export type ColumnKey =
  | "transactionTime"
  | "paymentRef"
  | "ftNumber"
  | "sourceName"
  | "sourceBank"
  | "beneficiaryName"
  | "beneficiaryBank"
  | "subject"
  | "internalDescription"
  | "debit"
  | "credit"
  | "balance"
  | "uniqueCode"
  | "businessUnit"
  | "subcategory"
  | "category"
  | "recipientAccount"
  | "sourceFile"
  | "financialAccount"
  | "financialMappingSource"
  | "profitTreatment"
  | "profitReason";

export type SortKey = "newest" | "oldest" | "amountDesc" | "amountAsc" | "codeAsc";

export const defaultColumns: ColumnKey[] = [
  "transactionTime",
  "paymentRef",
  "sourceName",
  "sourceBank",
  "beneficiaryName",
  "beneficiaryBank",
  "subject",
  "debit",
  "credit",
  "balance",
  "uniqueCode",
];

export const allColumns: ColumnKey[] = [
  ...defaultColumns,
  "ftNumber",
  "businessUnit",
  "category",
  "subcategory",
  "recipientAccount",
  "sourceFile",
  "internalDescription",
  "financialAccount",
  "financialMappingSource",
  "profitTreatment",
  "profitReason",
];

export const columnLabels: Record<ColumnKey, { id: string; en: string }> = {
  transactionTime: { id: "Waktu Transaksi", en: "Transaction Time" },
  paymentRef: { id: "No. Referensi", en: "Reference No." },
  ftNumber: { id: "FT Number", en: "FT Number" },
  sourceName: { id: "Nama Pengirim", en: "Sender Name" },
  sourceBank: { id: "Bank Pengirim", en: "Sender Bank" },
  beneficiaryName: { id: "Nama Penerima", en: "Recipient Name" },
  beneficiaryBank: { id: "Bank Penerima", en: "Recipient Bank" },
  subject: { id: "Deskripsi", en: "Description" },
  internalDescription: { id: "Deskripsi EventKita", en: "EventKita Description" },
  debit: { id: "Debet", en: "Debit" },
  credit: { id: "Kredit", en: "Credit" },
  balance: { id: "Saldo Riil", en: "Running Balance" },
  uniqueCode: { id: "Kode", en: "Code" },
  businessUnit: { id: "Unit Bisnis", en: "Business Unit" },
  subcategory: { id: "Subkategori", en: "Subcategory" },
  category: { id: "Kategori", en: "Category" },
  recipientAccount: { id: "Rekening", en: "Recipient account" },
  sourceFile: { id: "Sumber File", en: "Source File" },
  profitTreatment:{id:"Perhitungan Laba/Rugi",en:"Profit/Loss Treatment"},
  profitReason:{id:"Alasan Perhitungan",en:"Calculation Explanation"},
  financialAccount: {id:"Akun Laporan Efektif",en:"Effective Report Account"},
  financialMappingSource: {id:"Asal Akun Laporan",en:"Report Account Origin"},
};

export function transactionTime(item: Transaction) {
  return item.transactionTimestamp || (item.date ? `${item.date}T00:00` : "");
}

function compareTransactions(a: Transaction, b: Transaction, sort: SortKey) {
    if (sort === "oldest") return transactionTime(a).localeCompare(transactionTime(b));
    if (sort === "amountDesc") return b.amount - a.amount || transactionTime(b).localeCompare(transactionTime(a));
    if (sort === "amountAsc") return a.amount - b.amount || transactionTime(b).localeCompare(transactionTime(a));
    if (sort === "codeAsc") return a.uniqueCode.localeCompare(b.uniqueCode) || transactionTime(b).localeCompare(transactionTime(a));
    return transactionTime(b).localeCompare(transactionTime(a));
}
export function sortTransactions(rows: Transaction[], sort: SortKey) {
  return [...rows].sort((a, b) => compareTransactions(a, b, sort));
}
/** Preserve known order for edits with unchanged membership and sort keys.
 * Always return latest raw objects, including notes and server revision tokens.
 */
export function createTransactionSorter() {
  let previous: Transaction[] | undefined;
  let previousSort: SortKey | undefined;
  let order: number[] = [];
  return (rows: Transaction[], sort: SortKey): Transaction[] => {
    const unchanged = previousSort === sort && previous?.length === rows.length && rows.every((row, i) => {
      const old = previous![i];
      return old === row || (old.id === row.id && old.amount === row.amount && old.uniqueCode === row.uniqueCode && transactionTime(old) === transactionTime(row));
    });
    if (!unchanged) order = rows.map((_, i) => i).sort((a, b) => compareTransactions(rows[a], rows[b], sort));
    previous = rows; previousSort = sort;
    return order.map((i) => rows[i]);
  };
}

export function exportValue(item: Transaction, column: ColumnKey, language: Language, businessUnits?: BusinessUnit[], categories?: FinanceCategory[], resolveFinancial?: (row:Transaction)=>ResolvedFinancialAccount,resolveSimple?:(row:Transaction)=>SimpleProfitResolution): string | number {
  switch (column) {
    case "transactionTime": return transactionTime(item).replace("T", " ");
    case "paymentRef": return item.paymentRef;
    case "ftNumber": return item.ftNumber || "";
    case "sourceName": return item.sourceName || "";
    case "sourceBank": return item.sourceBank || "";
    case "beneficiaryName": return item.beneficiaryName || "";
    case "beneficiaryBank": return item.beneficiaryBank || "";
    case "subject": return item.subject;
    case "internalDescription": return item.internalDescription || "";
    case "debit": return item.direction === "DEBIT" ? item.amount : "";
    case "credit": return item.direction === "CREDIT" ? item.amount : "";
    case "balance": return item.balance ?? "";
    case "uniqueCode": return item.uniqueCode;
    case "businessUnit": return transactionBrandName(item, language, businessUnits);
    case "subcategory": return item.subcategory ? categoryName(item.subcategory, language, categories) : "";
    case "category": return categoryName(item.category, language, categories);
    case "recipientAccount": return recipientAccountLabel(item);
    case "sourceFile": return item.sourceFile;
    case "profitTreatment": return resolveSimple ? simpleTreatmentLabel(resolveSimple(item).treatment,language) : "";
    case "profitReason": return resolveSimple ? simpleReasonLabel(resolveSimple(item).reason,language) : "";
    case "financialAccount": { const result=resolveFinancial?.(item);return result ? (result.account ? accountName(result.account,language) : "") : item.financialAccount||""; }
    case "financialMappingSource": { const result=resolveFinancial?.(item);return result?financialSourceLabel(result.source,language):item.financialMappingSource||""; }
  }
}

export function exportMatrix(rows: Transaction[], columns: ColumnKey[], language: Language, businessUnits?: BusinessUnit[], categories?: FinanceCategory[], resolveFinancial?: (row:Transaction)=>ResolvedFinancialAccount,resolveSimple?:(row:Transaction)=>SimpleProfitResolution) {
  return [
    columns.map((column) => columnLabels[column][language]),
    ...rows.map((item) => columns.map((column) => exportValue(item, column, language, businessUnits, categories, resolveFinancial,resolveSimple))),
  ];
}
